import React from "react";
import { Drawer, Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { ClientInvestment, ClientInvestmentPayment } from "../types";

interface Props {
  investment: ClientInvestment | null;
  open: boolean;
  onClose: () => void;
  onAddManualPayment: (investmentId: string) => void;
  onMarkInstallment: (investmentId: string) => void;
  markingInstallmentId?: string | null;
  onEditInvestment: (investment: ClientInvestment) => void;
  onEditPayment: (investmentId: string, payment: ClientInvestmentPayment) => void;
  onDeletePayment: (investmentId: string, payment: ClientInvestmentPayment) => void;
  deletingPaymentId?: string | null;
}

const fmt = (n: number) => Math.round(n).toLocaleString();

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const AutoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const InvestmentDetailsDrawer: React.FC<Props> = React.memo(
  ({
    investment,
    open,
    onClose,
    onAddManualPayment,
    onMarkInstallment,
    markingInstallmentId,
    onEditInvestment,
    onEditPayment,
    onDeletePayment,
    deletingPaymentId,
  }) => {
    const { t } = useTranslation();

    if (!investment) return null;

    const done = investment.status === "COMPLETED";
    const isInstallments = investment.paymentType === "INSTALLMENTS";
    const pct = Math.round(investment.progress || 0);
    const locationName =
      typeof investment.locationId === "object" && investment.locationId
        ? investment.locationId.name
        : "";

    const payments = [...(investment.payments || [])].reverse();

    return (
      <Drawer
        title={investment.name}
        width={560}
        onClose={onClose}
        open={open}
        destroyOnClose
        className="ci-drawer"
        styles={{ body: { padding: 0 } }}
      >
        <div className="ci-drawer-body">
          {/* Stats row */}
          <div className="ci-dr-stats">
            <div className="ci-dr-stat">
              <div className="l">{t("Total")}</div>
              <div className="v">{fmt(investment.totalAmount)}</div>
            </div>
            <div className="ci-dr-stat paid">
              <div className="l">{t("Paid")}</div>
              <div className="v">{fmt(investment.paidAmount)}</div>
            </div>
            <div className="ci-dr-stat rem">
              <div className="l">{t("Remaining")}</div>
              <div className="v">
                {investment.remainingAmount > 0 ? fmt(investment.remainingAmount) : "0"}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="ci-dr-prog">
            <div className="bar">
              <i className={done ? "done" : ""} style={{ width: `${pct}%` }} />
            </div>
            <span className={`ci-dr-chip ${done ? "completed" : "active"}`}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block", marginRight: 4 }} />
              {done ? t("COMPLETED") : t("ACTIVE")}
            </span>
          </div>

          {/* Key-value details */}
          <div className="ci-kv">
            <div className="ci-kv-row">
              <span className="k">{t("Payment type")}</span>
              <span className="v">
                <span className={`ci-dr-chip ${isInstallments ? "installments" : "one-time"}`}>
                  {isInstallments ? t("Installments") : t("One-time")}
                </span>
              </span>
            </div>
            <div className="ci-kv-row">
              <span className="k">{t("Payment mode")}</span>
              <span className="v">
                <span className={`ci-dr-chip ${investment.paymentMode === "AUTO" ? "auto" : "manual"}`}>
                  {investment.paymentMode === "AUTO"
                    ? t("Auto deduct monthly")
                    : t("Manual payment tracking")}
                </span>
              </span>
            </div>
            <div className="ci-kv-row">
              <span className="k">{t("Investment start date")}</span>
              <span className="v mono">
                {investment.startDate ? dayjs(investment.startDate).format("YYYY-MM-DD") : "-"}
              </span>
            </div>
            {locationName && (
              <div className="ci-kv-row">
                <span className="k">{t("Location")}</span>
                <span className="v">{locationName}</span>
              </div>
            )}
            {investment.installmentsCount ? (
              <div className="ci-kv-row">
                <span className="k">{t("Number of installments")}</span>
                <span className="v mono">{investment.installmentsCount}</span>
              </div>
            ) : null}
            {isInstallments && investment.monthlyAmount ? (
              <div className="ci-kv-row">
                <span className="k">{t("Monthly amount")}</span>
                <span className="v mono">{fmt(investment.monthlyAmount)}</span>
              </div>
            ) : null}
            {investment.description ? (
              <div className="ci-kv-row">
                <span className="k">{t("Description")}</span>
                <span className="v" style={{ fontSize: 12, color: "var(--ci-muted)" }}>
                  {investment.description}
                </span>
              </div>
            ) : null}
          </div>

          {/* Action buttons */}
          <div className="ci-dr-actions">
            <button
              className="ci-dr-action-btn"
              onClick={() => onEditInvestment(investment)}
            >
              <EditIcon />
              {t("Edit")}
            </button>
            <button
              className="ci-dr-action-btn primary"
              disabled={investment.remainingAmount <= 0}
              onClick={() => onAddManualPayment(investment._id)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t("Add manual payment")}
            </button>
            {investment.remainingAmount > 0 && (
              <button
                className="ci-dr-action-btn"
                disabled={markingInstallmentId === investment._id}
                onClick={() => onMarkInstallment(investment._id)}
              >
                {markingInstallmentId === investment._id ? (
                  <Spin size="small" />
                ) : (
                  <CheckIcon />
                )}
                {isInstallments ? t("Mark installment as paid") : t("Mark payment as paid")}
              </button>
            )}
          </div>

          {/* Payment history */}
          <div className="ci-dr-sec-h">{t("Payment history")}</div>
          <div className="ci-dr-sec-sub">
            {payments.length}{" "}
            {payments.length === 1 ? t("payment") : t("payments")} · {t("total")}{" "}
            {fmt(investment.paidAmount)} RSD
          </div>

          {payments.length === 0 ? (
            <div className="ci-no-payments">{t("No payments yet")}</div>
          ) : (
            <div>
              {payments.map((p) => (
                <div className="ci-pay-item" key={p._id}>
                  <div className={`ci-pay-dot${p.type === "AUTO" ? " auto" : ""}`}>
                    {p.type === "AUTO" ? <AutoIcon /> : <CheckIcon />}
                  </div>
                  <div className="ci-pay-main">
                    <div className="ci-pay-amt">
                      {fmt(p.amount)}
                      <span className="date">{dayjs(p.paymentDate).format("YYYY-MM-DD")}</span>
                    </div>
                    <div className="ci-pay-desc">
                      <span className={`ci-pay-tag ${p.type === "AUTO" ? "auto" : "manual"}`}>
                        {p.type === "AUTO" ? t("Auto") : t("Manual")}
                      </span>
                      {p.note && <span>{p.note}</span>}
                    </div>
                  </div>
                  {p.type === "MANUAL" && (
                    <div className="ci-pay-tools">
                      <button
                        title={t("Edit")}
                        onClick={() => onEditPayment(investment._id, p)}
                      >
                        <EditIcon />
                      </button>
                      <button
                        className="del"
                        title={t("Delete")}
                        disabled={deletingPaymentId === p._id}
                        onClick={() => onDeletePayment(investment._id, p)}
                      >
                        {deletingPaymentId === p._id ? (
                          <Spin size="small" />
                        ) : (
                          <TrashIcon />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>
    );
  },
  (prev, next) =>
    prev.investment?._id === next.investment?._id &&
    prev.open === next.open &&
    prev.markingInstallmentId === next.markingInstallmentId &&
    prev.deletingPaymentId === next.deletingPaymentId &&
    prev.investment?.paidAmount === next.investment?.paidAmount &&
    prev.investment?.payments?.length === next.investment?.payments?.length,
);

InvestmentDetailsDrawer.displayName = "InvestmentDetailsDrawer";

export default InvestmentDetailsDrawer;
